import { AbstractControl, ValidationErrors, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { ThemingService } from 'src/app/theming.service';
import { Router } from '@angular/router';
import { LambdaService } from 'src/app/lambda.service';
import {
  extractShopCompanies,
  ShopCompany,
} from 'src/app/components/shop-companies/shop-company.models';
declare var $: any;

@Component({
  selector: 'app-create-client',
  templateUrl: './create-client.component.html',
  styleUrls: ['./create-client.component.scss']
})
export class CreateClientComponent implements OnInit {
  loading: boolean = false;
  clientForm: FormGroup;
  showPassword: boolean = false;
  username = '';
  nationalIdInvalid: boolean = false;
  placeholderText: string = '';
  clientSubscriptions: any[] = [];
  shopCompanies: ShopCompany[] = [];

  constructor(
    public themingService: ThemingService,
    private fb: FormBuilder,
    private router: Router,
    private lambdaService: LambdaService
  ) {
    this.clientForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      gender: [''],
      phone: [''],
      documentType: ['', Validators.required],
      clientSubscription: [''],
      shopCompanyId: [null],
      nationalId: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: [{ value: '', disabled: true }, [Validators.required, this.passwordValidator]],
      isPoolRegister: [false],
      isVerifiedClient: [false]
    });
  }

  ngOnInit(): void {
    this.handlePhoneInput();
    this.getSubscriptions();
    this.getShopCompanies();
    this.handleIsPoolRegisterToggle();
    this.clientForm.get('nationalId')?.valueChanges.subscribe(value => {
      this.validateNationalId(value);
    });

    this.clientForm.get('documentType')?.valueChanges.subscribe(type => {
    this.placeholderText = type === 'Passport'
      ? 'Ej: A12345678900'
      : (type === 'DIMEX'
        ? 'Ej: 12345678901'
        : (type === 'other'
          ? ''
          : 'Ej: 123456789'));
    this.validateNationalId(this.clientForm.get('nationalId')?.value || '');
  });
  }

  handlePhoneInput(): void {
    this.clientForm.get('phone')?.valueChanges.subscribe((value) => {
      if (value && !value.startsWith('+')) {
        this.clientForm.get('phone')?.setValue('+' + value, { emitEvent: false });
      }
    });
  }

  handleIsPoolRegisterToggle(): void {
    this.clientForm.get('isPoolRegister')?.valueChanges.subscribe((isChecked) => {
      if (isChecked) {
        this.clientForm.get('password')?.enable();
      } else {
        this.clientForm.get('password')?.disable();
        this.clientForm.get('password')?.reset();
        this.showPassword = false;
      }
    });
  }

  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);
    const isValidLength = password.length >= 8;

    return !(hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isValidLength) ? {
      passwordStrength: {
        hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, isValidLength
      }
    } : null;
  }

  getPasswordErrors(): string[] {
    const passwordControl = this.clientForm.get('password');
    const errors = passwordControl?.errors?.['passwordStrength'];
    const messages: string[] = [];

    if (errors) {
      if (!errors.hasUpperCase) messages.push('Debe contener al menos una letra mayúscula.');
      if (!errors.hasLowerCase) messages.push('Debe contener al menos una letra minúscula.');
      if (!errors.hasNumber) messages.push('Debe contener al menos un número.');
      if (!errors.hasSpecialChar) messages.push('Debe contener al menos un carácter especial.');
      if (!errors.isValidLength) messages.push('Debe tener al menos 8 caracteres.');
    }

    return messages;
  }

  generatePassword(): void {
    if (this.clientForm.get('password')?.enabled) {
      const length = 12;
      const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
      let password = "";
      for (let i = 0, n = charset.length; i < length; ++i) {
        password += charset.charAt(Math.floor(Math.random() * n));
      }
      this.clientForm.controls['password'].setValue(password);
    }
  }

  togglePasswordVisibility(): void {
    if (this.clientForm.get('password')?.enabled) {
      this.showPassword = !this.showPassword;
    }
  }

  onDocumentTypeChange(event: Event): void {
    const documentType = (event.target as HTMLSelectElement).value;
    this.clientForm.get('documentType')?.setValue(documentType);
    this.validateNationalId(this.clientForm.get('nationalId')?.value || '');
  }

  validateNationalId(value: string): void {
    value = value.trim();
    const documentType = this.clientForm.get('documentType')?.value;

    if (documentType === 'ID') {
      const isNumeric = /^\d+$/.test(value);
      const isValidLength = value.length === 9;
      this.nationalIdInvalid = value.length > 0 && (!isNumeric || !isValidLength);
    } else if (documentType === 'Passport') {
      this.nationalIdInvalid = !/^[A-Z][0-9]{8,11}$/.test(value);
    } else if (documentType === 'DIMEX') {
    this.nationalIdInvalid = !/^[1-9][0-9]{11}$/.test(value);
    } else if (documentType === 'other') {
      this.nationalIdInvalid = value.length === 0; // Inválido si está vacío
    } else {
      this.nationalIdInvalid = false;
    }

    this.clientForm.get('nationalId')?.setErrors(this.nationalIdInvalid ? { invalid: true } : null);
  }

  async getSubscriptions() {
    this.loading = true;
    try {
      let response: any = await this.lambdaService.getClientSubscriptions(this.themingService.shopId);
      this.clientSubscriptions = response.subscriptions;
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }

  async getShopCompanies() {
    try {
      const response: any = await this.lambdaService.getShopCompanies(
        this.themingService.shopId,
        true
      );
      this.shopCompanies = extractShopCompanies(response).filter(
        (company) => company.status === 1
      );
    } catch (error) {
      console.error('Error loading shop companies:', error);
      this.shopCompanies = [];
    }
  }

  createClient(): void {
    if (this.clientForm.valid) {
      this.loading = true;
      const clientData = this.clientForm.value;
      clientData.isVerifiedClient = clientData.isVerifiedClient ? 1 : 0;
      const shopCompanyId = this.normalizeShopCompanyId(
        clientData.shopCompanyId
      );

      this.lambdaService.createClient(
        this.themingService.shopId,
        clientData.firstName,
        clientData.lastName,
        clientData.phone,
        clientData.nationalId,
        clientData.gender || "",
        clientData.email,
        this.username || '',
        clientData.isPoolRegister,
        clientData.password,
        clientData.isVerifiedClient,
        clientData.documentType,
        clientData.clientSubscription,
        shopCompanyId ?? undefined
      ).then((data: any) => {
        this.loading = false;
        if (data.success === true) {
          $('#successClientModal').modal('show');
          $('#successClientModal').on('hidden.bs.modal', () => {
            this.clientForm.reset();
            this.router.navigate(['/clients']);
          });
        } else {
          this.showErrorModal();
        }
      }).catch((error) => {
        console.log(error);
        this.loading = false;
        this.showErrorModal();
      });
    } else {
      this.showRequiredFieldsModal();
    }
  }

  showErrorModal(): void {
    $('#errorClientModal').modal('show');
    $('#errorClientModal').on('hidden.bs.modal', () => {
      this.clientForm.markAsPristine();
    });
  }

  showRequiredFieldsModal(): void {
    $('#requiredClientModal').modal('show');
  }

  private normalizeShopCompanyId(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const id = Number(value);
    return Number.isFinite(id) ? id : undefined;
  }

  closeModal() {
    $('#successClientModal').modal('hide');
    $('#errorClientModal').modal('hide');
    $('#requiredClientModal').modal('hide');
  }
}
